// Live preview of a email-document template document. Resolves $brand and $slot tags
// against brand context + brand-derived defaults (recent media, product
// variantIds), then renders body children with the same React preview
// components the email builder uses. Same approach as SectionCardPreview,
// but for a whole document rather than a single section row that ships its
// own slot schema.
//
// Slot defaults are inferred from where each $slot.X reference appears in
// the tree (image-src → image URL, text node content → copy placeholder,
// etc.). Templates don't carry an explicit slot schema today, so context is
// the source of truth.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  resolveDefaultSectionRefs,
  resolveMergeTagsInTree,
  type BrandResolverContext,
  type EmailDocument,
  type EmailNode,
} from '@nexmail/shared-util/email-builder';
import { PreviewNode, type PreviewNodeBaseProps } from '@/components/email-builder/EmailBuilderPreview';
import { buildPreviewSlotResolver } from '@/lib/preview-slot-resolver';
import type { ResolvedDefaultSection } from '@/lib/use-default-sections';

interface Props {
  // Accepted loosely so callers don't need to cast through the generated
  // Orval type (which is `{ [key: string]: unknown }` — the OpenAPI schema
  // can't model EmailDocument's recursive union). We narrow at runtime via
  // isEmailDocument before walking.
  document: { [key: string]: unknown } | EmailDocument;
  brand: BrandResolverContext | undefined;
  // Same callback shape EmailBuilder hands SectionLibraryPanel — used by the
  // merge-tag resolver to fill image and product slots from the brand's
  // recent pool. Per-resolve dedupe set is owned here.
  pickRecentMedia: (slotName: string, used: Set<string>, offset?: number) => string | undefined;
  // Cutout-aware picker. When provided, slots that feed image nodes flagged
  // `productCutout: true` resolve through this first and only fall through
  // to pickRecentMedia if it returns undefined. Without this, cutout slots
  // pull from the generic recent-media pool and a lifestyle photo can land
  // where a transparent PNG was meant to be.
  pickTransparentMedia?: (slotName: string, used: Set<string>, offset?: number) => string | undefined;
  pickProduct: (slotName: string, used: Set<string>) => string | undefined;
  // Multi-output product picker. Required for templates that use dotted slot
  // refs (`$slot.productN.cutoutImage` / `.name` / `.price` / `.productUrl`)
  // — without it those refs stay literal in the preview.
  pickProductRich?: (slotName: string, used: Set<string>) => import('@nexmail/shared-util/email-builder').PickedProduct | undefined;
  // Per-card rotation seed — derive from a stable hash of the template id so
  // each card starts at a different point in the media pool. Without this,
  // every card previews the same first image in its first image slot.
  mediaOffset?: number;
  // Outer width the preview scales to fit. The document still composes at its
  // natural width (document.root.props.width) and is transform-scaled down.
  displayWidth?: number;
  // Cap on rendered height. In interactive mode the parent scrolls past
  // this; in non-interactive (card) mode we re-scale smaller so the whole
  // email fits inside.
  maxDisplayHeight?: number;
  // When true, the preview container captures scroll and pointer events so
  // the user can read long emails. False (the default) keeps the component
  // non-interactive — matches SectionCardPreview, useful as a thumbnail.
  interactive?: boolean;
  // When true, the outer container fills its parent's height (flex: 1)
  // instead of using a measured/capped height. Pair with interactive so
  // users can scroll inside a column that's already constrained by the
  // surrounding modal layout.
  fillContainerHeight?: boolean;
  // Pre-resolved default header / footer sections used to fill any
  // `$brand.defaultHeader` / `$brand.defaultFooter` placeholder section-refs
  // left in the template. Caller fetches once via useDefaultSectionsForScope
  // and passes the same object to every preview card on the page so we
  // don't re-query per card. Undefined slots cause placeholders to be
  // dropped silently — preview just renders without that header/footer.
  defaultHeader?: ResolvedDefaultSection;
  defaultFooter?: ResolvedDefaultSection;
}

function isEmailDocument(raw: unknown): raw is EmailDocument {
  if (!raw || typeof raw !== 'object') return false;
  const r = raw as Record<string, unknown>;
  return (
    'schemaVersion' in r &&
    'root' in r &&
    typeof r.root === 'object' &&
    r.root != null &&
    'children' in (r.root as Record<string, unknown>)
  );
}

const NOOP_SELECT = () => undefined;
const NOOP_ADD = () => undefined;

export function TemplateDocumentPreview({
  document,
  brand,
  pickRecentMedia,
  pickTransparentMedia,
  pickProduct,
  pickProductRich,
  mediaOffset,
  displayWidth = 480,
  maxDisplayHeight,
  interactive = false,
  fillContainerHeight = false,
  defaultHeader,
  defaultFooter,
}: Props) {
  const resolvedDoc = useMemo<EmailDocument | null>(() => {
    if (!isEmailDocument(document)) return null;
    const cloned = structuredClone(document);
    // Substitute default-section placeholders BEFORE the merge-tag pass.
    // resolveSectionForInsert (called internally by resolveDefaultSectionRefs)
    // resolves the section subtree's own $brand/$slot tags using the section's
    // own slotDefaults — so by the time the outer resolveMergeTagsInTree runs,
    // the inserted header/footer subtree is already fully resolved and the
    // outer pass is a no-op for those nodes (idempotent over already-resolved
    // strings).
    resolveDefaultSectionRefs(cloned, {
      defaultHeader,
      defaultFooter,
      brand,
      pickRecentMedia,
      pickTransparentProductMedia: pickTransparentMedia
        ? (slotName, used) => {
            const url = pickTransparentMedia(slotName, used, mediaOffset);
            // No productId paired with the URL here — section-level previews
            // can't roundtrip to PhotoRoom. Resolver gracefully skips
            // productId stamping when not provided.
            return url ? { url, productId: '' } : undefined;
          }
        : undefined,
      pickProduct,
      mediaOffset,
    });
    const slotResolver = buildPreviewSlotResolver({
      document: cloned,
      brand,
      pickRecentMedia,
      pickTransparentMedia,
      pickProduct,
      pickProductRich,
      mediaOffset,
    });
    resolveMergeTagsInTree(cloned, brand, slotResolver);
    return cloned;
  }, [
    document,
    brand,
    pickRecentMedia,
    pickTransparentMedia,
    pickProduct,
    pickProductRich,
    mediaOffset,
    defaultHeader,
    defaultFooter,
  ]);

  const bodyChildren = useMemo<EmailNode[]>(
    () => (resolvedDoc ? resolvedDoc.root.children.slice(1) : []),
    [resolvedDoc],
  );

  const emailWidth = resolvedDoc?.root.props.width ?? 600;

  const innerRef = useRef<HTMLDivElement | null>(null);
  const outerRef = useRef<HTMLDivElement | null>(null);
  const [naturalHeight, setNaturalHeight] = useState<number>(0);
  const [containerHeight, setContainerHeight] = useState<number>(0);

  // Re-measure after layout — same trick as SectionCardPreview. offsetHeight
  // ignores the transform so we can scale without double-counting.
  useLayoutEffect(() => {
    if (!innerRef.current) return;
    let frame = 0;
    let last = 0;
    let ticks = 0;
    const tick = () => {
      if (!innerRef.current) return;
      const h = innerRef.current.offsetHeight;
      if (h !== last) {
        last = h;
        setNaturalHeight(h);
      }
      ticks++;
      if (ticks < 120) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [resolvedDoc]);

  // Watch the outer container's height so fillContainerHeight mode can
  // scale-to-fit dynamically as the modal resizes. Use contentRect from
  // ResizeObserver (content box, excluding padding) rather than clientHeight
  // — the modal's preview column has p:3, and sizing to clientHeight would
  // make this box 48px taller than the content area, overflowing equally
  // top/bottom under the parent's alignItems:center and clipping the email's
  // header and footer.
  useEffect(() => {
    if (!fillContainerHeight) return;
    const el = outerRef.current?.parentElement;
    if (!el) return;
    const measure = () => {
      const cs = window.getComputedStyle(el);
      const padY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
      setContainerHeight(el.clientHeight - padY);
    };
    measure();
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry && entry.contentRect) {
        setContainerHeight(entry.contentRect.height);
      } else {
        measure();
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [fillContainerHeight]);

  // Three scaling modes:
  //   fillContainerHeight — fit-both into displayWidth × measured container
  //                         height so the entire email is visible at once
  //                         without scrolling. Used in the modal preview
  //                         where the user wants a full picture of the
  //                         template before generating.
  //   interactive=true    — fit-width, scroll vertically.
  //   else (cards)        — fit-both into displayWidth × maxDisplayHeight.
  const widthScale = displayWidth / emailWidth;
  const fitBothScale =
    !interactive && maxDisplayHeight && naturalHeight > 0
      ? Math.min(widthScale, maxDisplayHeight / naturalHeight)
      : widthScale;
  const containerFitScale =
    fillContainerHeight && containerHeight > 0 && naturalHeight > 0
      ? Math.min(widthScale, containerHeight / naturalHeight)
      : widthScale;
  const scale = fillContainerHeight
    ? containerFitScale
    : !interactive
      ? fitBothScale
      : widthScale;

  const scaledHeight = naturalHeight * scale;
  const outerHeight = fillContainerHeight
    ? containerHeight || scaledHeight
    : interactive
      ? maxDisplayHeight
        ? Math.min(scaledHeight, maxDisplayHeight)
        : scaledHeight
      : maxDisplayHeight ?? scaledHeight;

  if (!resolvedDoc || bodyChildren.length === 0) {
    return (
      <Box
        sx={{
          width: displayWidth,
          height: 220,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          borderRadius: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {resolvedDoc ? 'Empty template' : 'Preview unavailable'}
        </Typography>
      </Box>
    );
  }

  const previewProps: PreviewNodeBaseProps = {
    selectedNodeId: null,
    onSelectNode: NOOP_SELECT,
    onRequestAdd: NOOP_ADD,
    brandPrimaryColor: brand?.colorByUsage?.primary,
    brandLogoUrl: brand?.logoUrl,
    brandName: brand?.name,
  };

  // Visual (post-transform) footprint of the scaled email — what the user
  // actually sees. Used as the dimensions of the middle wrapper so flex
  // centering acts on the visual size, not the natural 600px flow width.
  const scaledInnerWidth = emailWidth * scale;
  const scaledInnerHeight = (naturalHeight || 1) * scale;

  // Only interactive (scroll) mode uses block layout. fillContainerHeight now
  // scales the whole email to fit the container, so it centers like a card.
  const blockMode = interactive && !fillContainerHeight;
  return (
    <Box
      ref={outerRef}
      sx={{
        width: displayWidth,
        height: outerHeight || 480,
        overflowY: blockMode ? 'auto' : 'hidden',
        overflowX: 'hidden',
        bgcolor: resolvedDoc.root.style?.backgroundColor || '#FFFFFF',
        borderRadius: 1,
        boxShadow: '0 4px 18px rgba(0,0,0,0.10)',
        position: 'relative',
        pointerEvents: interactive ? 'auto' : 'none',
        // blockMode (interactive + scrollable): top-anchored, no flex.
        // Otherwise: center the scaled email inside the box on both axes.
        display: blockMode ? 'block' : 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Sized wrapper carrying the post-transform footprint so the flex
          parent centers the visible size, not the un-scaled 600px box, and
          scroll extents in blockMode match the scaled content height. */}
      <Box
        sx={{
          position: 'relative',
          width: scaledInnerWidth,
          height: scaledInnerHeight || (blockMode ? 'auto' : undefined),
          flexShrink: 0,
        }}
      >
        <Box
          ref={innerRef}
          sx={{
            width: emailWidth,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
        >
          {bodyChildren.map((child) => (
            <PreviewNode key={child.id} node={child} {...previewProps} />
          ))}
        </Box>
      </Box>
    </Box>
  );
}
